<template>
  <div class="overflow-x-auto">
    <b-table
      ref="tableEntradas"
      :fields="visibleFields"
      :items="items"
      :sort-by.sync="pagination.orderBy"
      :sort-desc.sync="sortDesc"
      sort-icon-left
      :empty-text="$t('label.noData')"
      no-border-collapse
      show-empty
      borderless
      responsive
      @sort-changed="onSortingChange"
    >
      <div class="text-center loader my-2" slot="table-busy">
        <b-spinner class="align-middle" />
      </div>
      <template #head(actions-right)>
        <select-columns
          :fields.sync="fields"
          :pagination="pagination"
          :cod-table="columnsConfig.codTabla"
        />
      </template>
      <template #cell(actions-left)="row">
        <div class="table-button-container d-flex">
          <b-icon
            class="icon-details ml-3 my-auto cursor-pointer"
            icon="chevron-down"
            aria-label="Collapse Toggle"
            :flip-v="row.detailsShowing"
            @click="row.toggleDetails"
          />
        </div>
      </template>
      <template #row-details="row">
        <b-card class="detail-row">
          <detalle-movimiento
            :cliente="getCliente(row.item)"
            :movimiento="row.item"
            :salidas="false"
          />
        </b-card>
      </template>

      <template #cell(fecContable)="row">
        <span
          :id="`fecContable-${divisa}-${row.index}`"
          v-b-tooltip.bottom
          :title="filterLocalTime(row.item.fecContable)"
        >
          {{ row.value }}
        </span>
      </template>

      <template #cell(fecServicio)="row">
        <span
          :id="`fecServicio-${divisa}-${row.index}`"
          v-b-tooltip.bottom
          :title="filterLocalTime(row.item.fecServicio)"
        >
          {{ row.value }}
        </span>
      </template>

      <template #cell(fecMovimiento)="row">
        <span
          :id="`fecMovimiento-${divisa}-${row.index}`"
          v-b-tooltip.bottom
          :title="filterLocalTime(row.item.fecMovimiento)"
        >
          {{ row.value }}
        </span>
      </template>

      <!-- Right Actions Btns -->
      <template #cell(actions-right)="row">
        <div class="table-button-container" v-if="row.item.numDiferencia">
          <img
            :id="`diff-${divisa.codDivisa}-${row.index}`"
            src="@/assets/icons/diferencia-icon.svg"
            :alt="$t('label.diferencias')"
            class="icon-diferencia"
          />

          <b-tooltip
            :target="`diff-${divisa.codDivisa}-${row.index}`"
            custom-class="table-cell-tooltip"
            placement="bottom"
          >
            <div class="tooltip-inner-content-diff">
              <div>{{ $t("label.declarado") }}: {{ filterNumber(row.item.numDeclarado, 2) }}</div>
              <div>
                {{ $t("label.diferencia") }}:
                {{ filterNumber(row.item.numDiferencia, 2) }}
              </div>
            </div>
          </b-tooltip>
        </div>
      </template>
    </b-table>

    <div class="mt-3" v-if="totalElements > pagination.size">
      <pagination
        :current-page="pagination.page"
        :page-size="pagination.size"
        :total-elements="totalElements"
        @change="onChangeCurrentPage"
      ></pagination>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import VueFilters from "@/vue-filters";
import {
  ICliente,
  IConfColumnaPais,
  IDivisa,
  IEntrada,
  IField,
  ISaldo,
  ISalida,
  ISoConfColumnasUpdateDTO,
} from "@/entities";
import {
  EDirection,
  IColumnaResponse,
  IEntradaData,
  IEntradaParams,
  IMovimientoParams,
  IPaged,
} from "@/services";
import DetalleMovimiento from "../common/DetalleMovimiento.vue";
import Pagination from "@/components/common/Pagination.vue";
import SelectColumns from "@/components/common/SelectColumns/SelectColumns.vue";

@Component({
  components: { SelectColumns, DetalleMovimiento, Pagination },
  mixins: [VueFilters],
})
export default class EntradasTable extends Vue {
  @Prop({ required: true }) columnsConfig!: IColumnaResponse;
  @Prop({ required: false, default: null }) saldo!: ISaldo | null;
  @Prop({ required: false, default: null }) divisa!: IDivisa;
  @Prop({ required: false, default: null }) params!: IMovimientoParams;
  @Prop({ required: false, default: false }) isBCR!: boolean;
  @Watch("params", { deep: true })
  onChangeParams(): void {
    this.pagination.page = 1;
    this.getEntradas();
  }
  @Prop({ required: false, default: 0 }) total!: number;
  @Prop({ required: false, default: 0 }) totalElements!: number;
  @Prop({ required: false, default: true }) modal!: boolean;
  @Action("fetchEntradas") fetchEntradas!: (
    params: IEntradaParams
  ) => Promise<IEntradaData | undefined>;
  @Action("fetchEntradasBCR") fetchEntradasBCR!: (
    params: IEntradaParams
  ) => Promise<IEntradaData | undefined>;

  @Action("saveColumna") saveColumna!: (
    params: ISoConfColumnasUpdateDTO
  ) => Promise<IColumnaResponse | undefined>;

  @Getter("getClientesAll") getClientes!: ICliente[];

  public sortDesc = this.columnsConfig?.codTipoOrden !== "ASC";
  public pagination: IPaged = {
    direction: this.sortDesc ? EDirection.DESC : EDirection.ASC,
    orderBy: this.columnsConfig?.codOrden || "fecMovimiento",
    page: 1,
    size: 10,
  };

  entradaResponse: IEntradaData | null = null;

  getCliente(movimiento: IEntrada | ISalida): ICliente | undefined {
    return this.getClientes.find(
      (c) => c.id === Number(movimiento.puntoservicio.clientexdel.cliente.id)
    );
  }

  get items(): IEntrada[] {
    return this.entradaResponse?.entradas?.content || [];
  }

  get fields(): IField[] {
    const visibleKeys = this.columnsConfig?.codColumna?.split(",").filter((s) => s !== "") || [];
    let columnas: IField[] = [
      {
        key: "actions-left",
        label: "ACCIONES",
        sortable: false,
        thClass: "table-header table-header-active pl-4",
        tdClass: "table-cell-first",
        visible: true,
        class: "table-cell",
        aux: true,
        code: "",
      },
    ];
    this.columnsConfig.columnas?.forEach((column: IConfColumnaPais) => {
      columnas.push({
        key: column.keyFront,
        label: this.$t("label." + column.codColumna),
        sortable: true,
        thClass: "table-header",
        tdClass: (_value: unknown, key: string): string => `table-cell-middle ${key}`,
        thStyle: column.widthFront ? { width: column.widthFront } : {},
        visible: this.isVisible(visibleKeys, column.codColumna),
        class: "table-cell",
        aux: false,
        code: column.codColumna,
        formatter: (value: any): string => {
          if (column.formatType == "Date") {
            return this.filterLocalDate(value);
          } else if (column.formatType == "Number") {
            return this.filterNumber(value, 2);
          }
          return value;
        },
      });
    });
    columnas.push({
      key: "actions-right",
      label: "",
      sortable: false,
      thClass: "table-header table-header-add-columnas pb-0",
      tdClass: "table-cell-middle actions",
      visible: true,
      class: "table-cell",
      aux: true,
      code: "",
    });
    return columnas;
  }

  set fields(fields: IField[]) {
    this.columnsConfig.codColumna =
      fields
        .filter((item) => item.visible && !item.aux)
        .map((item) => item.code)
        .join(",") || "";
  }

  get visibleFields(): IField[] {
    return this.fields.filter((item) => item.visible);
  }

  isVisible(visibleKeys: string[], column: string): boolean {
    return !visibleKeys.length || !!visibleKeys.find((key) => key === column);
  }

  mounted(): void {
    this.getEntradas();
  }

  async getEntradas(): Promise<void> {
    let params: IMovimientoParams;
    if (this.saldo) {
      params = {
        pkSaldo: this.saldo.id,
      };
    } else {
      params = { ...this.params };
      params.pkDivisa = this.divisa.id;
    }
    params.page = this.pagination.page! - 1;
    params.size = this.pagination.size;
    params.orderBy = this.pagination.orderBy;
    params.direction = this.pagination.direction;

    if (this.isBCR) {
      this.entradaResponse = (await this.fetchEntradasBCR(params)) || null;
    } else {
      this.entradaResponse = (await this.fetchEntradas(params)) || null;
    }
    this.$emit("update:total", this.entradaResponse?.totalEntrada || 0);
    this.$emit("update:totalElements", this.entradaResponse?.entradas.totalElements || 0);
  }

  onChangeCurrentPage(currentPage: number): void {
    this.pagination.page = currentPage;
    this.getEntradas();
  }

  onSortingChange(): void {
    this.$nextTick(async () => {
      this.pagination.direction = this.sortDesc ? EDirection.DESC : EDirection.ASC;
      await this.getEntradas();
      await this.saveColumnsConfig();
    });
  }

  async saveColumnsConfig(): Promise<void> {
    const params: ISoConfColumnasUpdateDTO = {
      codColumna: this.columnsConfig.codColumna || "",
      codTabla: this.columnsConfig.codTabla,
    };
    if (this.pagination) {
      params.codTipoOrden = this.pagination.direction;
      params.numPagElements = this.pagination.size;
      params.codOrden = this.pagination.orderBy;
    }
    await this.saveColumna(params);
  }
}
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}
.table-responsive {
  padding-left: 20px;
  margin-left: -20px;
  padding-right: 20px;
  width: 102%;

  &::v-deep {
    .comentarioAuto,
    .comentarioManual {
      white-space: pre-line;
    }
  }
}
</style>
